/** Une ligne de la ressource react-admin "billing-account-dw-usage". */
export interface BillingAccountRow {
	/** UUID du billing account. */
	id: string;
	name: string;
	billingPlanName: string;
	/** Slug du plan : on reconnaît un Enterprise là-dessus, jamais sur le libellé. */
	billingPlanSlug: string;
	subscriptionStatus: string;
	/**
	 * Quanti paie un datawarehouse sans abonnement actif en face — le signal
	 * central de l'écran.
	 *
	 * ⚠️ Calculé par le back, jamais recalculé ici, même si la règle paraît
	 * triviale : c'est le MÊME booléen qui trie les lignes, teinte le montant et
	 * alimente le compteur du bandeau. Recalculé côté front, il afficherait un
	 * jour « 9 comptes » au-dessus de 7 lignes rouges. Un compte sans aucun
	 * abonnement (statut vide) est en alerte, comme un `canceled`.
	 */
	alert: boolean;
	/** Projets non supprimés, managés ou non. */
	projectCount: number;
	managedProjectCount: number;
	deletedProjectCount: number;
	/**
	 * Process le plus récent parmi les projets non supprimés du compte. `null` =
	 * aucun process, jamais — et pas « aucun sur la plage » : la mesure ignore le
	 * filtre de période.
	 */
	lastProcessAt: string | null;
	storageEur: number;
	totalEur: number;
	/**
	 * ⚠️ Somme des journées mesurées de TOUS les projets du compte : 12 peut
	 * vouloir dire 3 projets × 4 jours. Ne jamais l'afficher comme une durée à ce
	 * niveau — seul son passage à zéro a un sens, « rien n'a été mesuré ».
	 */
	measuredDays: number;
}

/** Une ligne de la ressource react-admin "project-dw-usage". */
export interface ProjectUsageRow {
	id: number;
	folderId: string;
	name: string;
	status: number;
	/** Faux = datawarehouse du client : le projet ne coûte rien à Quanti. */
	managed: boolean;
	connectorCount: number;
	storageEur: number;
	totalEur: number;
	/** Ici, et ici seulement, c'est bien un nombre de journées. */
	measuredDays: number;
	/** `null` = ce projet n'a jamais rien fait tourner. */
	lastProcessAt: string | null;
}

/** Une ligne de la ressource react-admin "orphan-dw-usage" : projet effacé en dur. */
export interface OrphanUsageRow {
	id: number | string;
	folderId: string;
	storageEur: number;
	totalEur: number;
	measuredDays: number;
	firstMeasuredAt: string | null;
	lastMeasuredAt: string | null;
}

/** qm_projects.status = 90 : provisioning GCP échoué. */
export const PROJECT_STATUS_CREATE_FAILED = 90;

/** qm_projects.status = 500 : projet soft-deleted. */
export const PROJECT_STATUS_DELETE = 500;

export const PLAN_SLUG_ENTERPRISE = 'enterprise';

export const SUBSCRIPTION_ACTIVE = 'active';

export const SUBSCRIPTION_PAST_DUE = 'past_due';

/**
 * Au-delà, un projet est dormant. Ce qui vit tourne quotidiennement : en prod
 * rien ne se situe entre 1 et 100 jours, donc le seuil exact ne départage rien
 * aujourd'hui. Il ne sert qu'à teinter une date, jamais à porter un badge.
 */
export const PROCESS_STALE_DAYS = 7;

export interface Badge {
	key: string;
	label: string;
	/** Couleur MUI. `default` = un fait à connaître, pas une alerte. */
	color: 'error' | 'warning' | 'default';
}

export const isEnterprisePlan = (slug: string): boolean => slug === PLAN_SLUG_ENTERPRISE;

export const isSubscriptionActive = (status: string): boolean => status === SUBSCRIPTION_ACTIVE;

/**
 * Une pastille ne se justifie que si elle appelle un geste : le nombre de
 * projets et le statut d'abonnement se lisent déjà dans leurs colonnes. Une ligne sans problème ne porte donc AUCUN badge — pas de « Rien à
 * signaler », qui occuperait l'œil pour dire qu'il n'y a rien à voir.
 */
export const accountBadges = (record: BillingAccountRow): Badge[] => {
	const badges: Badge[] = [];
	if (record.subscriptionStatus === SUBSCRIPTION_PAST_DUE) {
		badges.push({ key: 'pastDue', label: 'Paiement en échec', color: 'error' });
	}
	// Un seul badge sur le sujet, et il ne concerne QUE le « jamais » : « il y a
	// 105 j » se lit déjà dans la colonne d'à côté, et une pastille qui redit sa
	// colonne ne se justifie pas.
	if (!record.lastProcessAt) {
		badges.push({ key: 'noProcess', label: 'Jamais aucun process', color: 'error' });
	}
	// Pas une alerte : c'est le cadre de facturation, et il explique pourquoi la
	// dépense de cette ligne est en gris.
	if (isEnterprisePlan(record.billingPlanSlug)) {
		badges.push({ key: 'enterprise', label: 'Forfait', color: 'default' });
	}
	return badges;
};

export const projectBadges = (project: ProjectUsageRow): Badge[] => {
	if (project.status === PROJECT_STATUS_DELETE) {
		return [{ key: 'deleted', label: 'Supprimé', color: 'default' }];
	}
	if (project.status === PROJECT_STATUS_CREATE_FAILED) {
		return [{ key: 'createFailed', label: 'Création échouée', color: 'warning' }];
	}
	// Après les deux statuts : sur un projet supprimé ou jamais créé, « jamais
	// aucun process » n'apprend rien.
	if (!project.lastProcessAt) {
		return [{ key: 'noProcess', label: 'Jamais aucun process', color: 'error' }];
	}
	return [];
};

export const ORPHAN_BADGE: Badge = {
	key: 'orphan',
	label: 'Projet supprimé, compte perdu',
	color: 'error',
};
